import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import useActiveChat from "@web-src/features/chats/hooks/useActiveChat";
import ChatInput, {
  ChatInputHandle,
  ChatInputParams,
} from "@web-src/features/chats/components/ChatInput";
import {
  ChatMessage,
  ChatMessageActionType,
  ChatMessageType,
  ChatType,
} from "@web-src/features/chats/types";
import {
  getWorkspaceParticipantDisplayImg,
  getWorkspaceParticipantDisplayName,
} from "@web-src/features/workspaces/utils";
import { usePrevious, cx } from "@jugl-web/utils";
import { useDispatch, useSelector } from "react-redux";
import { selectUserId } from "@web-src/features/auth/authSlice";
import ChatMessageSideBar from "@web-src/features/workspaces/components/ChatMessageSideBar";
import PeopleInfoSidebar from "@web-src/features/people/components/PeopleInfoSidebar";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider";
import { ChatInfoSideBar } from "@web-src/modules/chats/components/ChatInfoSideBar/ChatInfoSideBar";
import ChatHeader from "../../../../modules/chats/components/ChatHeader";
import useSendMessage from "../../hooks/useSendMessage";
import useConversations from "../../hooks/useConversations";
import ChatMessagesWindow from "../ChatMessagesWindow";
import useMessageActions from "../../hooks/useMessageActions";
import MessageActionItem from "../MessageActionItem";
import useEditMessage from "../../hooks/useEditMessage";
import ForwardSideBar from "../ForwardSideBar";
import DeleteSideBar from "../DeleteSideBar";
import { ChatMessagesWindowHandle } from "../ChatMessagesWindow/ChatMessagesWindow";
import {
  selectOpenedUserProfile,
  setOpenedUserProfile,
  selectSearchSidebarIsOpen,
} from "../../chatsSlice";
import { ChatMessageSearchSidebar } from "../ChatMessageSearchSidebar";
import { ChatMessageProvider } from "../../providers/ChatMessageProvider";

const Chat: React.FC = () => {
  const dispatch = useDispatch();

  const openedUserProfile = useSelector(selectOpenedUserProfile);
  const searchSidebarIsOpen = useSelector(selectSearchSidebarIsOpen);
  const { chat, isParticipant, participants } = useActiveChat();
  const { subscriptionInfo } = useEntityProvider();
  const { addNewMessage } = useConversations();
  const sendMessage = useSendMessage();
  const editMessage = useEditMessage();
  const {
    openedChatMessage,
    closeMessageInfo,
    cancelMessageAction,
    messageAction,
  } = useMessageActions(chat?.id);
  const [selectedMessages, setSelectedMessages] = useState<ChatMessage[]>([]);
  const [chatInfoSideBarIsOpen, setChatInfoSideBarIsOpen] = useState(false);

  const meId = useSelector(selectUserId);
  const mentions = useMemo(
    () =>
      participants
        ?.filter(({ user_id }) => meId !== user_id)
        ?.map((item) => ({
          id: item.user_id,
          name: getWorkspaceParticipantDisplayName(item),
          img: getWorkspaceParticipantDisplayImg(item),
        })),
    [meId, participants]
  );

  const $inputRef = useRef<ChatInputHandle>(null);
  const $chatWindowRef = useRef<ChatMessagesWindowHandle>(null);
  const [isSending, setIsSending] = useState<boolean>();

  const handleCancelMessageAction = useCallback(() => {
    if (messageAction?.type === ChatMessageActionType.edit) {
      $inputRef.current?.clear();
    }
    cancelMessageAction();
  }, [cancelMessageAction, messageAction?.type]);

  useEffect(() => {
    if (
      messageAction?.type === ChatMessageActionType.edit &&
      messageAction?.message
    ) {
      $inputRef.current?.setValue(
        messageAction.message.payload?.body ||
          messageAction.message.payload?.attachments?.[0]?.caption ||
          "",
        messageAction.message
      );
    }
    if (
      messageAction?.type &&
      [ChatMessageActionType.edit, ChatMessageActionType.reply].includes(
        messageAction?.type
      )
    ) {
      $inputRef.current?.focus();
    }
  }, [messageAction?.message, messageAction?.type]);

  const handleSendClick: ChatInputParams["onSubmit"] = useCallback(
    async ({ body, file, gif, attachmentType, conference }) => {
      if (!chat?.id) {
        return;
      }
      if (
        messageAction?.message &&
        messageAction?.type === ChatMessageActionType.edit
      ) {
        setIsSending(true);
        editMessage({
          msgId: messageAction.message.msg_id,
          body,
          attachment: messageAction.message.payload?.attachments?.[0],
          chatId: chat.id,
        })
          .then(() => {
            cancelMessageAction();
            $inputRef?.current?.clear();
          })
          .finally(() => {
            setIsSending(false);
          });
        return;
      }
      sendMessage({
        to: chat.id,
        type:
          chat.type === ChatType.muc
            ? ChatMessageType.muc
            : ChatMessageType.chat,
        body,
        file,
        action: messageAction,
        gif,
        attachmentType,
        conference,
      }).then((item) => {
        if (item.type === "success") {
          if (!chat.id) {
            return;
          }
          addNewMessage?.({
            id: chat.id,
            message: item.message as ChatMessage,
            outgoing: true,
          });
          $chatWindowRef.current?.scrollToBottom();
        }
      });
      if (subscriptionInfo?.actionsAllowed) {
        $inputRef?.current?.clear();
      }
      if (messageAction) {
        cancelMessageAction();
      }
      setTimeout(() => {
        $chatWindowRef.current?.scrollToBottom();
      }, 100);
    },
    [
      addNewMessage,
      cancelMessageAction,
      chat?.id,
      chat?.type,
      editMessage,
      messageAction,
      sendMessage,
      subscriptionInfo,
    ]
  );

  // re-mount chat component, to make sure there is no left owers from previous chat state
  // TODO: find better solution
  const [chatTemporairlyHidden, setChatTemporairlyHidden] = useState<boolean>();
  const previousActiveChat = usePrevious(chat);
  useEffect(() => {
    if (previousActiveChat && chat?.id !== previousActiveChat?.id) {
      setChatTemporairlyHidden(true);
      setTimeout(() => {
        setChatTemporairlyHidden(false);
      }, 0);
    }
  }, [chat, previousActiveChat]);

  const closeUserProfile = useCallback(() => {
    dispatch(setOpenedUserProfile());
  }, [dispatch]);

  const onChatHeaderClick = useMemo(
    () =>
      chat?.id &&
      (chat?.type === ChatType.chat ||
        (chat?.type === ChatType.muc && isParticipant))
        ? () => setChatInfoSideBarIsOpen(true)
        : undefined,
    [chat, isParticipant, setChatInfoSideBarIsOpen]
  );

  const showChatSideBar = useMemo(
    () =>
      chatInfoSideBarIsOpen &&
      chat?.id &&
      (chat?.type === ChatType.chat ||
        (chat?.type === ChatType.muc && isParticipant)),
    [chatInfoSideBarIsOpen, chat, isParticipant]
  );

  if (!chat) {
    return null;
  }
  return (
    <>
      <div className="flex-1 overflow-x-auto">
        <div className="relative flex h-full flex-1 flex-col">
          <ChatHeader onClick={onChatHeaderClick} />
          <div className="relative w-full flex-1">
            {chat && !chatTemporairlyHidden ? (
              <ChatMessagesWindow
                ref={$chatWindowRef}
                chat={chat}
                disableMessageActions={chat?.type === "muc" && !isParticipant}
                selectedMessages={selectedMessages}
                setSelectedMessages={setSelectedMessages}
                messageAction={messageAction}
              />
            ) : null}
          </div>
          {!chat.deleted && (chat.type === ChatType.chat || isParticipant) && (
            <div className="border-t-1 border-0 border-t border-solid border-gray-200 px-4 py-2 shadow">
              {messageAction?.type === ChatMessageActionType.edit ||
              messageAction?.type === ChatMessageActionType.reply ? (
                <ChatMessageProvider message={messageAction.message}>
                  <MessageActionItem
                    message={messageAction.message}
                    action={messageAction.type}
                    onCancel={handleCancelMessageAction}
                  />
                </ChatMessageProvider>
              ) : undefined}
              {chat?.id && (
                <ChatInput
                  chatId={chat.id}
                  onSubmit={handleSendClick}
                  ref={$inputRef}
                  mentions={mentions}
                  isLoading={isSending}
                  disableAttachments={
                    messageAction?.type === ChatMessageActionType.edit
                  }
                />
              )}
            </div>
          )}
          {openedChatMessage && (
            <ChatMessageSideBar
              message={openedChatMessage}
              isOpen
              workspaceId={chat?.type === ChatType.muc ? chat?.id : undefined}
              onRequestClose={closeMessageInfo}
            />
          )}
        </div>
      </div>
      {showChatSideBar && !chat.deleted && (
        <div className={cx({ hidden: searchSidebarIsOpen === true })}>
          <ChatInfoSideBar onClose={() => setChatInfoSideBarIsOpen(false)} />
        </div>
      )}
      {openedUserProfile && (
        <PeopleInfoSidebar
          userId={openedUserProfile}
          isOpen
          onClose={closeUserProfile}
        />
      )}
      {messageAction?.type === ChatMessageActionType.delete && (
        <DeleteSideBar
          messages={selectedMessages}
          onRequestClose={() => {
            cancelMessageAction();
            setSelectedMessages([]);
          }}
        />
      )}
      {messageAction?.type === ChatMessageActionType.forward && (
        <ForwardSideBar
          messages={selectedMessages}
          onRequestClose={() => {
            cancelMessageAction();
            setSelectedMessages([]);
          }}
        />
      )}
      {searchSidebarIsOpen && <ChatMessageSearchSidebar />}
      {openedUserProfile && (
        <PeopleInfoSidebar
          userId={openedUserProfile}
          isOpen
          onClose={closeUserProfile}
        />
      )}
    </>
  );
};

export default Chat;
